import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { officialCategoryName } from "@intelligems/ig-utils/common";

/**
 * The point in an experience's lifecycle at which we are warning the merchant
 * about deleting Shopify themes. `launch` is the pre-launch Confirm QA modal,
 * where the merchant has not yet ended or paused anything, so the warning has
 * to cover both future outcomes.
 */
export type ThemeWarningContext = "launch" | "pause" | "end";

/**
 * Minimum number of days a theme must stay in Shopify after a theme experience
 * stops, and the window we actually recommend. Returning shoppers hold an
 * Intelligems cookie pointing at their assigned theme, so deleting it inside
 * this window sends them to a theme that no longer exists.
 */
export const THEME_RETENTION_MINIMUM_DAYS = 30;
export const THEME_RETENTION_RECOMMENDED_DAYS = 90;

/**
 * "A", "A or B", "A, B, or C" — the themes read as a single subject ("the A or
 * B theme"), matching the approved copy.
 */
export const formatThemeNameList = (themeNames: string[]): string => {
  if (themeNames.length === 0) {
    return "";
  }
  if (themeNames.length === 1) {
    return themeNames[0]!;
  }
  if (themeNames.length === 2) {
    return `${themeNames[0]} or ${themeNames[1]}`;
  }
  return `${themeNames.slice(0, -1).join(", ")}, or ${themeNames.at(-1)}`;
};

/**
 * The themes as the subject of a sentence. Falls back to a generic phrase when
 * we could not resolve the names, so the warning is never weaker than before.
 */
const themeSubject = (
  themeNames: string[],
  category: ExperienceCategory
): string =>
  themeNames.length > 0
    ? `the ${formatThemeNameList(themeNames)} theme`
    : `the themes used in this ${officialCategoryName(category, false)}`;

/**
 * How to refer to the experience having stopped, in a subordinate clause.
 * Theme personalizations are "stopped" rather than paused.
 */
const stoppedClause = (
  context: ThemeWarningContext,
  category: ExperienceCategory
): string => {
  const categoryName = officialCategoryName(category, false);
  const stopWord = category === "personalization" ? "stopped" : "paused";

  switch (context) {
    case "launch":
      return `the ${categoryName} ends or is ${stopWord}`;
    case "pause":
      return `the ${categoryName} is ${stopWord}`;
    case "end":
      return `the ${categoryName} ends`;
  }
};

export const themeWarningTitle = (
  context: ThemeWarningContext,
  category: ExperienceCategory,
  themeNames: string[]
): string => {
  const base = `Do not delete ${themeSubject(themeNames, category)} in Shopify for ${THEME_RETENTION_MINIMUM_DAYS} days`;

  if (context === "launch") {
    const categoryName = officialCategoryName(category, false);
    const stopWord = category === "personalization" ? "stopping" : "pausing";
    return `${base} after ending or ${stopWord} this ${categoryName}`;
  }

  return base;
};

export const themeWarningDescription = (
  context: ThemeWarningContext,
  category: ExperienceCategory
): string => {
  const categoryName = officialCategoryName(category, false);
  const window = `${THEME_RETENTION_MINIMUM_DAYS} days minimum, ${THEME_RETENTION_RECOMMENDED_DAYS} days recommended.`;

  switch (context) {
    case "launch":
      return `Keep them live through the ${categoryName} and after you end or ${category === "personalization" ? "stop" : "pause"} it: ${window}`;
    case "pause":
      return `Keep them live after ${category === "personalization" ? "stopping" : "pausing"}: ${window}`;
    case "end":
      return `Keep them live after the ${categoryName} ends: ${window}`;
  }
};

/**
 * The checkbox the merchant has to tick. Names the concrete consequence —
 * returning shoppers hitting a broken storefront until their cookie expires —
 * rather than the vague "can break my storefront".
 */
export const themeAcknowledgementLabel = (
  context: ThemeWarningContext,
  category: ExperienceCategory,
  themeNames: string[]
): string =>
  `I understand that if I delete ${themeSubject(themeNames, category)} in Shopify, even after ${stoppedClause(context, category)}, returning shoppers may see a broken storefront until their cookie expires.`;
